import { memo, useCallback, useMemo, useState } from 'react';

import type { OnValidate } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/shadcn/accordion';
import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';

import { CodeEditor } from '@/components/monaco/codeEditor/codeEditor';
import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { Tray } from '@/components/ui/tray/tray';
import { TRAY_PORTAL_RIGHT } from '@/constant/htmlIds';
import {
  applyCodeChangesToActions,
  type CodeChanges,
  extractExecJsEffects,
  groupEffectsByAction
} from './effectsViewer.utils';

type Props = {
  actions: Record<string, unknown> | null | undefined;
  onClose: () => void;
  onSubmit: (partialConfig: {
    actions: Record<string, unknown>;
  }) => Promise<void>;
};

export const EffectsViewer = memo(({ actions, onClose, onSubmit }: Props) => {
  const [codeChanges, setCodeChanges] = useState<CodeChanges>(new Map());

  const [editorErrors, setEditorErrors] = useState<Map<string, boolean>>(
    new Map()
  );

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const groupedEffects = useMemo(
    () => groupEffectsByAction(extractExecJsEffects(actions)),
    [actions]
  );

  const hasEffects = groupedEffects.length > 0;
  const hasUnsavedChanges = codeChanges.size > 0;
  const hasErrors = [...editorErrors.values()].some(Boolean);

  const changedActionsCount = codeChanges.size;

  const getCodeValue = useCallback(
    (actionKey: string, effectIndex: number, originalCode: string): string => {
      const actionChanges = codeChanges.get(actionKey);
      if (actionChanges?.has(effectIndex)) {
        return actionChanges.get(effectIndex) ?? originalCode;
      }
      return originalCode;
    },
    [codeChanges]
  );

  const handleCodeChange = useCallback(
    (actionKey: string, effectIndex: number, originalCode: string) =>
      (newValue: string) => {
        setCodeChanges(prev => {
          const newChanges = new Map(prev);
          let actionChanges = newChanges.get(actionKey);

          if (newValue === originalCode) {
            if (actionChanges) {
              actionChanges.delete(effectIndex);
              if (actionChanges.size === 0) {
                newChanges.delete(actionKey);
              }
            }
          } else {
            if (!actionChanges) {
              actionChanges = new Map();
              newChanges.set(actionKey, actionChanges);
            }
            actionChanges.set(effectIndex, newValue);
          }

          return newChanges;
        });
      },
    []
  );

  const handleValidate = useCallback(
    (actionKey: string, effectIndex: number): OnValidate =>
      (markers: editor.IMarker[]) => {
        const key = `${actionKey}-${effectIndex}`;
        const hasError = markers.some(m => m.severity === 8);

        setEditorErrors(prev => {
          const newErrors = new Map(prev);
          if (hasError) {
            newErrors.set(key, true);
          } else {
            newErrors.delete(key);
          }
          return newErrors;
        });
      },
    []
  );

  const handleClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleSave = useCallback(async () => {
    if (!actions) return;
    if (!hasUnsavedChanges) return;

    setIsSubmitting(true);
    try {
      const newActions = applyCodeChangesToActions(actions, codeChanges);
      await onSubmit({ actions: newActions });
      setCodeChanges(new Map());
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [actions, codeChanges, hasUnsavedChanges, onSubmit]);

  return (
    <>
      <Tray
        onClose={handleClose}
        portalEl={document.getElementById(TRAY_PORTAL_RIGHT)}
        title="Effects Viewer"
        width={700}
      >
        <div className="flex h-full flex-col">
          <div className="flex-1 overflow-auto p-4">
            {hasEffects ? (
              <Accordion
                className="w-full"
                collapsible
                defaultValue={groupedEffects.at(0)?.actionKey}
                type="single"
              >
                {groupedEffects.map(
                  ({ actionKey, actionTitle, actionIcon, effects }) => (
                    <AccordionItem key={actionKey} value={actionKey}>
                      <AccordionTrigger className="px-2">
                        <div className="flex items-center gap-2">
                          <DynamicIcon icon={actionIcon} size={18} />
                          <span className="text-sm">{actionTitle}</span>
                          <span className="text-muted-foreground text-xs">
                            ({effects.length} effect
                            {effects.length > 1 ? 's' : ''})
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-2 pt-2">
                        <div className="flex flex-col gap-4">
                          {effects.map(({ index, code }) => (
                            <div
                              className="overflow-hidden rounded-md"
                              key={index}
                            >
                              {effects.length > 1 && (
                                <div className="border-x border-t bg-muted px-3 py-1.5 text-muted-foreground text-xs">
                                  Effect #{index + 1}
                                </div>
                              )}
                              <CodeEditor
                                className="min-h-[300px]"
                                height="300px"
                                language="javascript"
                                onChange={handleCodeChange(
                                  actionKey,
                                  index,
                                  code
                                )}
                                onValidate={handleValidate(actionKey, index)}
                                options={{
                                  minimap: { enabled: false },
                                  formatOnPaste: true,
                                  formatOnType: true
                                }}
                                value={getCodeValue(actionKey, index, code)}
                              />
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )
                )}
              </Accordion>
            ) : (
              <div className="flex h-full items-center justify-center text-muted-foreground">
                No execJs effects found in this workflow configuration.
              </div>
            )}
          </div>

          <div className="flex h-[70px] w-full shrink-0 items-center justify-end gap-4 border-t p-4">
            <Button
              aria-label="Close the effects viewer"
              data-cy="effects-viewer-close-btn"
              onClick={handleClose}
              variant="outline"
            >
              CLOSE
            </Button>
            <Button
              aria-label="Save changes"
              data-cy="effects-viewer-save-btn"
              disabled={hasErrors || !hasUnsavedChanges || isSubmitting}
              onClick={() => setShowConfirmModal(true)}
              variant={hasErrors ? 'destructive' : 'default'}
            >
              SAVE
            </Button>
          </div>
        </div>
      </Tray>

      {/* Save Confirmation Modal */}
      <Dialog onOpenChange={setShowConfirmModal} open={showConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Changes</DialogTitle>
            <DialogDescription>
              Are you sure you want to update the workflow effects? This will
              modify {changedActionsCount} action
              {changedActionsCount > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowConfirmModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={isSubmitting} onClick={handleSave}>
              {isSubmitting ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discard Changes Modal */}
      <Dialog onOpenChange={setShowDiscardModal} open={showDiscardModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard Changes?</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to close without
              saving?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowDiscardModal(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button onClick={onClose} variant="destructive">
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});
